import { LoginFactorType } from '@getstep/proto/common'
import { AuthLevel } from '@getstep/proto-lib/rpc'
import { AuthenticationFailureError } from '@getstep/sdk/dist/http/auth/AuthenticationFailureError'
import {
    ErrorCode,
    ErrorStatus,
    isHttpError,
} from '@getstep/sdk/dist/http/HttpError'
import { UnauthenticatedFeatureStore } from '@getstep/sdk/dist/store/UnauthenticatedFeatureStore'
import { mustExist, notNullish } from '@getstep/sdk/dist/util/Assert'
import { differenceInYears, parseISO } from 'date-fns'
import { pick } from 'lodash'
import {
    action,
    computed,
    makeAutoObservable,
    observable,
    transaction,
} from 'mobx'
import { v4 as uuidv4 } from 'uuid'

import analyticsService from '@lib/analytics/AnalyticsService'
import { ATTRIBUTION_PARAM_KEYS } from '@lib/analytics/constants'
import { getMetaPixelTraits } from '@lib/analytics/metaPixel'
import { AuthorizedStore } from '@lib/store/authorized/AuthorizedStore'
import { tokenStore } from '@lib/store/JwtTokenStore'
import type { LinkingStore } from '@lib/store/linking/LinkingStore'
import type { LocalFeatureOverridesStorage } from '@lib/store/LocalFeatureOverrideStore'
import type { SdkClientStore } from '@lib/store/SdkClientStore'
import {
    EmailVerificationStore,
    PhoneVerificationStore,
} from '@lib/store/session'
import { RecoveryStore } from '@lib/store/session/RecoveryStore'
import { LoginStatus } from '@lib/utils/login'

export class SessionStore {
    recoveryStore?: RecoveryStore
    authorizedStore?: AuthorizedStore
    loaded = false
    otpSessionId?: string
    private _unauthenticatedFeatureStore?: UnauthenticatedFeatureStore
    private clientId: string

    constructor(
        readonly sdk: SdkClientStore,
        private linkingStore: LinkingStore,
        readonly localFeatureOverrideStore: LocalFeatureOverridesStorage,
    ) {
        // Generate or retrieve clientId for unauthenticated feature store
        this.clientId = this.getOrCreateClientId()
        makeAutoObservable(this, {
            phoneVerificationStore: computed({ keepAlive: true }),
            emailVerificationStore: computed({ keepAlive: true }),
            hasVerifiedEmail: computed,
            loggedIn: computed,
            authorizedStore: observable,
            loaded: observable,
            login: action,
            logout: action,
        })
    }

    get loggedIn(): boolean {
        return !!this.authorizedStore?.userStore
    }

    get hasVerifiedEmail(): boolean {
        return !!this.authorizedStore?.userStore?.verifiedEmailAlias
    }

    get phoneVerificationStore() {
        return new PhoneVerificationStore(this.sdk)
    }

    get emailVerificationStore() {
        if (notNullish(this.authorizedStore?.userStore)) {
            return new EmailVerificationStore(
                this.sdk,
                mustExist(
                    this.authorizedStore?.userStore,
                    'userStore required to create EmailVerificationStore',
                ),
            )
        }
        return
    }

    async getUnauthenticatedFeatureStore() {
        if (!this._unauthenticatedFeatureStore) {
            const publicApi = await this.sdk.createPublicApi()
            this._unauthenticatedFeatureStore = new UnauthenticatedFeatureStore(
                this.clientId,
                publicApi,
            )
            await this._unauthenticatedFeatureStore.load()
        }
        return this._unauthenticatedFeatureStore
    }

    restore() {
        return transaction(async () => {
            try {
                console.info('attempting to login user')
                // means we have authorization saved in LocalStorage
                await this.login()
            } catch (e) {
                console.error(e)
                // let's clear everything out so we can at least reattempt to login
                console.info('failed to login, logging out user')
                this.logout()
            } finally {
                this.loaded = true
            }
        })
    }

    login = async (): Promise<LoginStatus> =>
        transaction(async () => {
            const userId = mustExist(
                tokenStore.refreshToken?.subject,
                'user id should be set in tokenStore to log in',
            )

            if (tokenStore.authLevel === AuthLevel.RESTRICTED) {
                const otpSessionId = this.otpSessionId
                if (notNullish(otpSessionId)) {
                    this.recoveryStore = await RecoveryStore.create(
                        otpSessionId,
                        this.sdk,
                    )
                    await this.recoveryStore.load()

                    // Check if user has unsupported factors (only PHONE and EMAIL are supported in web)
                    const supportedFactors = [
                        LoginFactorType.PHONE,
                        LoginFactorType.EMAIL,
                    ]
                    const hasUnsupportedFactor =
                        this.recoveryStore.session?.factors.some(
                            (factor) => !supportedFactors.includes(factor.type),
                        )
                    const hasEmailFactor =
                        this.recoveryStore.session?.factors.find(
                            (factor) => factor.type === LoginFactorType.EMAIL,
                        ) !== undefined

                    if (hasUnsupportedFactor && !hasEmailFactor) {
                        return LoginStatus.UNSUPPORTED_FACTOR
                    }

                    await this.recoveryStore.useEmailBasedRecovery()
                }
                return LoginStatus.IN_RECOVERY
            }

            try {
                // Create and load restricted store.
                this.authorizedStore = await AuthorizedStore.create(
                    this,
                    this.linkingStore,
                    this.sdk,
                    this.localFeatureOverrideStore,
                )

                await this.authorizedStore?.familyStore?.load()
                // Check if this is a recovery flow and user is not account owner or under 18
                if (!this.isAccountOwner() || this.isUserUnder18()) {
                    return LoginStatus.NOT_ACCOUNT_OWNER
                }

                // Identify user with full attributes after authorizedStore is loaded
                const attributionData = analyticsService.getAttributionData()

                // Get Meta Pixel traits (will read from cookies or construct from fbclid)
                const metaPixelTraits = getMetaPixelTraits(
                    attributionData.fbclid as string | undefined,
                )

                const userAttributes = {
                    // User profile data
                    email: this.authorizedStore?.userStore?.emailAlias?.value,
                    phone: this.authorizedStore?.userStore?.phoneAlias?.value,
                    firstName:
                        this.authorizedStore?.userStore?.user?.legalName
                            ?.firstName,
                    lastName:
                        this.authorizedStore?.userStore?.user?.legalName
                            ?.lastName,
                    dateOfBirth: this.authorizedStore?.userStore?.user?.dob, // For Braze
                    birthday: this.authorizedStore?.userStore?.user?.dob, // For Segment (reserved trait)
                    // Attribution parameters (UTM, click IDs, Meta Pixel)
                    ...pick(attributionData, ATTRIBUTION_PARAM_KEYS),
                    ...metaPixelTraits,
                }

                await analyticsService.identifyUser(userId, userAttributes)

                return LoginStatus.SUCCESS
            } catch (e) {
                if (!(e instanceof Error)) throw e

                if (e instanceof AuthenticationFailureError) {
                    return LoginStatus.FAIL
                }

                if (
                    isHttpError(e, {
                        status: ErrorStatus.PERMISSION_DENIED,
                        code: ErrorCode.VERIFIED_PHONE_REQUIRED,
                    })
                ) {
                    return LoginStatus.NO_PHONE
                }

                throw e
            }
        })

    logout = () =>
        transaction(() => {
            tokenStore.clearStorage()
            this.authorizedStore = undefined
        })

    getClientId(): string {
        return this.clientId
    }

    async isUnauthenticatedFeatureEnabled(feature: string): Promise<boolean> {
        try {
            const store = await this.getUnauthenticatedFeatureStore()
            // Feature is enabled if it exists in the assignments
            return store.features.some(
                (assignment) => assignment.feature === feature,
            )
        } catch (error) {
            console.warn(
                'Failed to check unauthenticated feature:',
                feature,
                error,
            )
            return false
        }
    }

    private isAccountOwner(): boolean {
        if (
            !this.authorizedStore?.familyStore ||
            !this.authorizedStore?.userStore
        ) {
            return false
        }

        // Find the current user's family member record
        const currentUserFamilyMember =
            this.authorizedStore.familyStore.members?.find(
                ({ user }) =>
                    user.id === this.authorizedStore?.userStore.user.id,
            )
        return currentUserFamilyMember?.isOwner ?? false
    }

    private isUserUnder18(): boolean {
        const user = this.authorizedStore?.userStore.user
        const dateOfBirth = user?.dob

        if (!dateOfBirth) {
            // If no date of birth is available, we can't determine age
            return false
        }

        try {
            // Parse the date of birth and calculate age using date-fns
            const birthDate = parseISO(dateOfBirth)
            const age = differenceInYears(new Date(), birthDate)

            return age < 18
        } catch (error) {
            // If date parsing fails, assume user is of age to avoid blocking
            console.warn('Failed to parse date of birth:', dateOfBirth, error)
            return false
        }
    }

    private getOrCreateClientId(): string {
        const CLIENT_ID_KEY = 'step_client_id'

        // Try to get existing clientId from localStorage
        if (typeof window !== 'undefined') {
            const existing = localStorage.getItem(CLIENT_ID_KEY)
            if (existing) {
                return existing
            }
        }

        // Generate new UUID and store it
        const newClientId = uuidv4()
        if (typeof window !== 'undefined') {
            localStorage.setItem(CLIENT_ID_KEY, newClientId)
        }

        return newClientId
    }
}
